export const PrivacyPolicyCheckWorks = () => {
	return (
		<span>
			<h1>Privacy Policy</h1>
			
			<p>Last Updated: July 11, 2025</p>

			<p>CheckWorks, Inc. ("CheckWorks," "we," "us," or "our") respects your privacy. This Privacy Notice
				("Notice") describes the processing of Personal Information (defined below) that is provided, collected,
				or disclosed while providing our products or services to you ("Services") and on the websites,
				applications, and online platforms that link to this Notice (collectively, "Site"), including when you
				apply for a job with us. It also describes rights you may have under applicable laws. Please read this
				Notice carefully to understand our policies and practices regarding your Personal Information and how we
				will treat it.</p>

			<p><strong>Personal Information We Collect</strong></p>

			<p>We collect several categories of Personal Information from and about users of our Site and Services.
				"Personal Information" means information that uniquely identifies, relates to, describes, or is
				reasonably capable of being associated with or linked to you. The categories of Personal Information we
				collect may include:</p>

			<ul>
				<li>
					<p>Contact Information - If you submit an inquiry or support request, register for an account, order
						checks, or request a free evaluation or demo, we may collect your contact information including
						your name, mailing address, email address, phone number, and financial institution.</p>
				</li>
				<li>
					<p>Commercial Information - If you order checks, we may collect commercial information including
						information about your purchases.</p>
				</li>
				<li>
					<p>Financial Information - When you order checks, we collect your bank account information including
						your routing number, account number, bank name, and check start number. We also collect payment
						card information to process your orders.</p>
				</li>
				<li>
					<p>Usage Information - When you use our Site, we may automatically record information, including
						your Internet Protocol address (IP Address), geolocation of your device, browser type, referring
						URLs (e.g., the website you visited before coming to our Site), domain names associated with
						your internet service provider, and any other information regarding your interaction with our
						Site.</p>
				</li>
				<li>
					<p>Employment Information - If you apply for employment with us, we will collect Personal
						Information related to your potential employment, including your education and employment
						history, address and contact information, demographic information, and any other information
						included in your resume or application.</p>
				</li>
				<li>
					<p>Communication Information - We may collect Personal Information contained within your
						communications with us via email, social media, telephone, or otherwise, and in certain cases we
						may use third-party service providers to do so. Where permitted by applicable law, we may
						collect and maintain records of calls and chats with our agents, representatives, or
						employees.</p>
				</li>
			</ul>

			<p><strong>How We Collect Personal Information</strong></p>

			<ul>
				<li>
					<p>Directly From You - We collect Personal Information that you provide to us directly, for example,
						if you choose to contact us, submit a support request, request information from us, sign up to
						receive updates, or otherwise utilize our Site or Services.</p>
				</li>
				<li>
					<p>From Third Parties - We may collect Personal Information from third parties, including but not
						limited to business partners, advertising networks, social networks, data analytics providers,
						mobile device providers, Internet or mobile service providers, recruiters and job application
						portals, and background check providers.</p>
				</li>
				<li>
					<p>Through Online Tracking Technologies - We use cookies and similar technologies to collect
						Personal Information automatically as you navigate our Site. For additional information
						regarding our use of these technologies, see the Cookies and Tracking Technologies section
						below.</p>
				</li>
			</ul>

			<p><strong>How We Use Personal Information</strong></p>

			<p>To the extent permitted by applicable law, we use Personal Information:</p>

			<ul>
				<li>
					<p>To provide and personalize our Site and Services, such as processing or fulfilling orders and
						transactions, providing and personalizing our Services, processing payments, providing customer
						service, maintaining or servicing accounts, verifying customer information, and creating and
						maintaining business records.</p>
				</li>
				<li>
					<p>To optimize, improve, and maintain our Site, including understanding how users interact with our
						Site, gauging user interest in certain Site functionality, and troubleshooting problems.</p>
				</li>
				<li>
					<p>For internal research and development, such as testing, verifying, and improving the quality of
						our Services or developing new ones.</p>
				</li>
				<li>
					<p>For marketing and advertising, including using your information to send you messages, notices,
						newsletters, surveys, promotions, or event invitations about our own or third parties' goods and
						services that may be of interest to you. We also use Personal Information to conduct
						interest-based advertising as discussed in the Cookies and Other Tracking Technologies section
						below. You can unsubscribe from any marketing emails that we may send you by following the
						instructions included in the email correspondence.</p>
				</li>
				<li>
					<p>For communicating with you, such as responding to your questions and comments or notifying you of
						changes to our Site or Services.</p>
				</li>
				<li>
					<p>For legal, security, or safety reasons, such as protecting our and our users' safety, property,
						or rights; complying with legal requirements; enforcing our terms, conditions, and policies;
						detecting, preventing, and responding to security incidents; and protecting against malicious,
						deceptive, fraudulent, or illegal activity.</p>
				</li>
				<li>
					<p>As part of a corporate transaction, such as in connection with the sale of part or all of our
						assets or business, the acquisition of part or all of another business or another business'
						assets, or another corporate transaction, including bankruptcy.</p>
				</li>
				<li>
					<p>To fulfill any other purpose for which you provide it, including purposes described when you
						provide the information or give your consent.</p>
				</li>
			</ul>

			<p>If you are applying for employment with us, we also use Personal Information to process your job
				application, to verify the information you have provided in your application, conduct interviews,
				perform background and reference checks, to communicate with you and answer your questions, to confirm
				your eligibility for employment, and improve our recruiting processes. We may also save your Personal
				Information for future employment opportunities with us.</p>

			<p><strong>How We Disclose Personal Information</strong></p>

			<p>We may disclose aggregated information about our users, and information that does not identify any
				individual, without restriction.</p>

			<p>We may disclose your Personal Information with your consent or in the following circumstances:</p>

			<ul>
				<li>
					<p>Employees and Other Personnel - We may share Personal Information with our employees and
						personnel (such as contractors) who have a need to know the information for our business
						purposes.</p>
				</li>
				<li>
					<p>Affiliates and Subsidiaries - We may share Personal Information within our family of
						companies for their and our business and marketing purposes, including providing you with
						information about the Services we think may be of interest to you.</p>
				</li>
				<li>
					<p>Service Providers - We disclose your Personal Information with the service providers that we
						use to support our business, including but not limited to, data analytics providers, website
						hosting providers, and other technology providers. If you are applying for a job with us, this
						may include service providers such as background check providers and human resource
						providers.</p>
				</li>
				<li>
					<p>Business Partners - We may disclose Personal Information with trusted business partners. For
						example, we may disclose your Personal Information with a company whose products or services we
						think may be of interest to you or who we co-sponsor a promotion or service with.</p>
				</li>
				<li>
					<p>Ad Tech Companies and Other Providers - We may share or make available limited Personal
						Information (such as a mobile device identifiers) with ad tech companies and other online
						service providers. When we share your Personal Information in this context, we follow applicable
						legal requirements, which may require that we provide opt-out rights or other individual
						rights.</p>
				</li>
				<li>
					<p>Legal Obligation or Safety Reasons - We may disclose Personal Information to a third party
						when we have a good faith belief that such disclosure of Personal Information is reasonably
						necessary to (a) satisfy or comply with any requirement of law, regulation, legal process, or
						enforceable governmental request, (b) enforce or investigate a potential violation of any
						agreement you have with us, (c) detect, prevent, or otherwise respond to fraud, security or
						technical concerns, (d) support auditing and compliance functions, or (e) protect the rights,
						property, or safety of CheckWorks, its employees and customers, or the public against harm.</p>
				</li>
				<li>
					<p>Merger or Change of Control - We may disclose Personal Information to third parties as
						necessary if we are involved in a merger, acquisition, or any other transaction involving a
						change of control in our business, including but not limited to, a bankruptcy or similar
						proceeding. Where legally required, we will give you notice prior to such disclosure.</p>
				</li>
				<li>
					<p>Other - We may disclose Personal Information to third parties when explicitly requested by
						or consented to by you, or for the purposes for which you disclosed the Personal Information to
						us as indicated at the time and point of the disclosure (or as was obvious at the time and point
						of disclosure).
					</p>
				</li>
			</ul>

			<p><strong>Cookies and Other Tracking Technologies</strong></p>

			<p>We and our service providers may use cookies and similar technologies to collect usage and browser
				information about how you use our Site. The technologies we use for this automatic data collection may
				include cookies and web beacons that permit us to verify system and server integrity and generate
				statistics around the popularity of certain content. We process the information collected through such
				technologies, which may include or be combined with Personal Information, to help operate certain
				features of our Site, to enhance your experience through personalization, and to help us better
				understand the features of our Site that you and other users are most interested in.</p>

			<p>Website Delivery and Appearance - We may use third-party providers to enable certain customer
				interaction opportunities, content delivery (like audio or video), or other service capabilities.
				Examples include, but are not limited to, the following functionality:</p>

			<ul>
				<li>
					<p>Content Delivery: We partner with service providers to host our Site and deliver specific content
						in support of our Services. For example, we use Google Fonts to serve fonts on our Site, which
						is governed by <a href="https://policies.google.com/privacy">Google's Privacy Policy</a>.</p>
				</li>
			</ul>

			<p>Website Analytics and Session Replay - We use analytics and session replay services, that use
				cookies and other technologies that collect your Personal Information, to assist us with analyzing our
				Site traffic and site usage to optimize, maintain, and secure our Site and inform subsequent business
				decisions (including, e.g., advertising). These include, but are not limited to, the following
				third-party services:</p>

			<ul>
				<li>
					<p>Google Analytics: To learn more about how Google uses data, visit
						<a href="https://policies.google.com/privacy">&#32;Google's Privacy Policy</a> and Google's page on "
						<a href="https://www.google.com/policies/privacy/partners">How Google uses data from sites or
							apps that use our services</a>." You may download the
						<a href="https://tools.google.com/dlpage/gaoptout">&#32;Google Analytics Opt-out Browser Add-on&#32;</a>
						for each web browser you use, but this does not prevent the use of other
						analytics tools.To learn more about Google Analytics cookies, visit
						<a href="https://support.google.com/analytics/answer/11397207?hl=en">&#32;Google Analytics Cookie
							Usage on Websites</a>.</p>
				</li>
				<li>
					<p>Meta Pixels: We use Meta pixels to track user activity on our Site and improve downstream
						offerings, including interest-based advertising for our services and those of our partners and
						service providers.</p>
				</li>
			</ul>

			<p>Interest-Based Advertising - We may also allow or enable third parties to collect Personal
				Information to provide their interest-based advertising on behalf of our products and services, or their
				own. Interest-based advertising occurs when advertisements are shown to you based on information
				collected from your online interactions over time and across multiple websites, devices, or online
				services that you visit or use. Some companies may engage in cross-context behavioral advertising to
				predict your preferences and show you advertisements that are most likely to be of interest or relevant
				to you.</p>

			<p>We do not control these third parties' collection or use of your information for these purposes, or the
				opt-out options they may individually offer you via their terms, conditions, and privacy policies. If
				you have any questions about an advertisement or other targeted content, you should contact the
				responsible provider directly. Examples of the third-party service providers we engage to serve
				interest-based advertisements include Google Ads (including Google Dynamic Remarketing) and Meta Ads
				(including Meta Pixels and Facebook advertising services). It is in this context that we may provide
				advertising networks, data analytics providers, social networks, and video sharing platforms with
				Personal Information such as your IP address, device information, Internet and other electronic network
				activity information, and geolocation information in the last twelve months.</p>

			<p>Selling Personal Information - While we do not sell Personal Information in exchange for monetary
				consideration, we do disclose Personal Information for other benefits that could be deemed a "sale"
				under various data protection laws because it is sometimes broadly defined to include activities such as
				the delivery of interest-based advertising on websites or allowing third parties to receive certain
				information, such as cookies, IP address, and/or browsing behavior.</p>

			<p>Cookie Choices - To manage your preferences with respect to these technologies, you can:</p>

			<ul>
				<li>
					<p>Change your cookie preferences by clicking the "Cookie Preferences" icon at the bottom of our
						Site or customize your browser settings to refuse all or some browser cookies, or to alert you
						when websites set or access cookies. If you disable certain cookies, please note that some parts
						of our Site may not function properly. These settings may be lost and require reconfiguration if
						you delete your cookies.</p>
				</li>
				<li>
					<p>Block the collection and use of your information by online platforms and ad tech companies for
						the purpose of serving interest-based advertising by visiting the opt out pages of the
						self-regulatory programs of which those companies are members:
						<a href="https://thenai.org/opt-out/">&#32;National Advertising Initiative</a> and
						<a href="https://optout.aboutads.info/?c=2&lang=EN">&#32;Digital Advertising Alliance</a>. Please
						note that even if you opt out of interest-based advertising, you may still see "contextual" ads
						which are based on the context of what you are looking at on the websites and pages you visit.
					</p>
				</li>
				<li>
					<p>Review and execute any provider-specific instructions to customize your preferences or opt-out of
						certain processing, including interest-based advertising, by third-party service providers. For
						example, to opt-out of this type of advertising by Google, customize your ad preferences, or
						limit Google's collection or use of your data, visit
						<a href="https://safety.google/intl/en_us/">&#32;Google's Safety Center</a> and
						<a href="https://www.google.com/settings/ads">&#32;Google's Ad Settings</a> and follow
						<a href="https://support.google.com/ads/answer/2662922?hl=en&ref_topic=7048998">&#32;Google's
							personalized ad opt-out instructions</a>. To opt out of advertising under Adobe Audience
						Manager, please follow <a href="https://www.adobe.com/privacy/opt-out.html#customeruse">Adobe's
							Experience Cloud opt-out instructions</a>. Meta also offers an
						<a href="https://www.facebook.com/help/109378269482053/?helpref=hc_fnav">&#32;Ad Preferences
							center&#32;</a>
						to customize your settings, as well as a
						<a href="https://www.facebook.com/privacy/policy/?section_id=6-HowCanYouManage">&#32;Privacy
							Policy&#32;</a>
						with additional information on how you can exercise your rights.</p>
				</li>
			</ul>

			<p><strong>How Long We Keep Your Personal Information</strong></p>

			<p>We retain your Personal Information for as long as needed: (i) to conduct business with you; (ii) fulfill
				the purposes outlined in this Notice; and (iii) to comply with our legal obligations, resolve disputes,
				and enforce any agreements. Criteria we will use to determine how long to retain your Personal
				Information include the nature and length of our business relationship with you; our legal rights,
				obligations, and retention requirements; and if we have an ongoing business purpose for retaining your
				Personal Information, such as communicating with you about ongoing or prospective Services you
				requested.</p>

			<p><strong>Links to Third-Party Websites</strong></p>

			<p>We are not responsible for the practices employed by any websites or services linked to or from our Site,
				including the information or content contained within them. We encourage you to investigate and ask
				questions before disclosing Personal Information to third parties, since any Personal Information
				disclosed will be handled in accordance with the applicable third party's privacy policy.</p>

			<p>In some cases, we offer links to social media platforms (like LinkedIn, Facebook, X (formerly known as
				Twitter) and Instagram) that enable you to easily connect with us or share information on social media.
				Any content you post via these social media pages is subject to the Terms of Use and Privacy Policies
				for those platforms.</p>

			<p><strong>International Use</strong></p>

			<p>If you are visiting our Site from outside of the United States, please note that our Site is hosted in
				the United States. Where permitted by applicable law, we may transfer the personal data we collect about
				you to the United States and other jurisdictions that may not be deemed to provide the same level of
				data protection as your home country, as necessary for the purposes set out in this Notice.</p>

			<p><strong>How We Protect Personal Information</strong></p>

			<p>We have implemented commercially reasonable measures designed to secure your Personal Information from
				accidental loss and from unauthorized access, use, alteration, and disclosure. Unfortunately, the
				transmission of information via the internet is not completely secure. Despite these efforts to store
				Personal Information in a secure environment, we cannot guarantee the security of Personal Information
				during its transmission or its storage on our systems.</p>

			<p><strong>Exercising Your Privacy Rights</strong></p>

			<p>Depending on where you live, you may have the following rights with respect to your Personal Information
				under applicable data protection laws:</p>

			<ul>
				<li>
					<p>Access - The right to request access to and obtain a copy of any Personal Information we may
						have about you.</p>
				</li>
				<li>
					<p>Deletion - The right to delete your Personal Information that we have collected or obtained,
						subject to certain exceptions.</p>
				</li>
				<li>
					<p>Correction - The right to request that we correct any inaccuracies in your Personal
						Information, subject to certain exceptions.</p>
				</li>
				<li>
					<p>Opt Out of Certain Processing - The right to: (a) opt out of the processing of your Personal
						Information for purposes of targeted or cross-context behavioral advertising, (b) opt out of the
						sale of your Personal Information, and (c) limit the use of your sensitive Personal Information
						(if applicable). We do not collect or process sensitive Personal Information outside of purposes
						permitted by law (such as section 7027(m) under the California Consumer Privacy Act
						Regulations), so we do not offer the option to limit its use.</p>
				</li>
				<li>
					<p>Automated Decision-Making - The right to know when you are subject to automated
						decision-making, the Personal Information used to render the decision, the principal factors and
						parameters involved in the decision, and human review or correction of the decision (or its
						underlying data, where appropriate). You may also have the right, depending on your location, to
						consent or opt out of (a) this automated decision-making or (b) any profiling in furtherance of
						decisions that produce legal or similarly significant effects. We do not profile you or other
						individuals in a manner that would result in legal or similarly significant effects, or engage
						in automated decision-making, so we do not offer an opt out of such activity.</p>
				</li>
				<li>
					<p>Lodge a Complaint - The right to lodge a complaint with a regulatory agency if you believe
						we have violated any of the rights afforded to you under applicable data protection laws. We
						encourage you to first reach out to us so we have an opportunity to address your concerns
						directly before you do so.</p>
				</li>
			</ul>

			<p>To exercise any of the privacy rights afforded to you under applicable data protection laws, please
				e-mail us at <a href="mailto:customerservice@checkworks.com\">customerservice@checkworks.com</a> or call
				us at (866) 870-5918. You may also exercise your opt out rights by broadcasting an Opt-Out Preference
				Signal, such as the Global Privacy Control (GPC). We honor Opt-Out Preference Signals, including GPC. If
				you choose to use an Opt-Out Preference Signal, you will need to turn it on for each supported browser
				or browser extension you use.</p>

			<p>You will not be discriminated against in any way by virtue of your exercise of the rights listed in this
				Notice. However, if you choose not to provide certain Personal Information, we may be unable to provide
				some, or all, of our Services to you.</p>

			<p>Only you, or an authorized agent that you authorize to act on your behalf, may make a request related to
				your Personal Information. We may need to verify your identity before fulfilling certain requests, and
				if we cannot verify your identity, we may request additional information from you. If you are an
				authorized agent making certain requests on behalf of another person, we will also need to verify your
				identity, which may require proof of your written authorization or evidence of power of attorney. We
				endeavor to respond to requests within the time period required by applicable law. If we require more
				time, we will inform you of the reason and extension period in writing.</p>

			<p>We do not charge a fee to process or respond to your requests unless they are excessive or repetitive. If
				we determine that a request warrants a fee, we will tell you why we made that decision and provide you
				with a cost estimate before completing your request. We may deny certain requests, or only fulfill some
				in part, as permitted or required by law. If you are not satisfied with the resolution of your request
				and you are afforded a right to appeal such decision, you will be notified of our appeal process in our
				response to your request.</p>

			<p><strong>Children's Privacy</strong></p>

			<p>We do not knowingly collect or solicit any Personal Information from children, as defined under
				applicable law, without verified written parental consent, and we have no actual knowledge of selling
				such Personal Information of minors under 16 years of age. If we learn that we have collected Personal
				Information from a child, we will promptly take steps to delete that information. If you believe we
				might have any information from or about a child, please contact us at
				<a href="mailto:customerservice@checkworks.com\">&#32;customerservice@checkworks.com</a>.</p>

			<p><strong>California Residents</strong></p>

			<p>This section provides additional information regarding ChecksForFree's practices pursuant to the
				California Consumer Privacy Act of 2018 and its implementing regulations, as amended by the California
				Privacy Rights Act ("CCPA"), where "Personal Information" has the definition set forth in the CCPA.</p>

			<p>Please see the chart below for detailed information about the categories of Personal Information we have
				collected from California residents during the twelve months preceding the date on which this Notice was
				last updated and the categories of third parties to whom we Sell or Share Personal Information (as those
				terms are defined in the CCPA). For each category of Personal Information set forth in the chart below,
				the categories of third parties to whom we disclose it for a business or commercial purpose are set
				forth in the How We Disclose Personal Information section above.</p>

			<table className="table table-bordered table-striped">
				<thead>
				<tr>
					<td width="50%">
						<p><strong>Personal Information Category set forth in Cal. Civ. Code § 1798.140</strong></p>
					</td>
					<td width="50%">
						<p><strong>Categories of Third Parties to Whom We Sell or Share Personal Information</strong>
						</p>
					</td>
				</tr>
				</thead>
				<tbody>
				<tr>
					<td>
						<p>Personal identifiers, including real name, alias, postal address, unique personal identifier,
							online identifier, Internet Protocol address, email address, or other similar
							identifiers.</p>
					</td>
					<td>
						<p>Third-party marketers and other third parties that set cookies and other online tracking
							technologies on our Site.</p>
					</td>
				</tr>
				<tr>
					<td>
						<p>California Customer Records Personal Information (Cal. Civ. Code § 1798.80(e)), including
							name, signature, physical characteristics or description, address, telephone number,
							education, employment, employment history, etc.</p>
					</td>
					<td>
						<p>We do not Sell or Share this category of Personal Information.</p>
					</td>
				</tr>
				<tr>
					<td>
						<p>Characteristics of protected classifications under California or federal law. This may
							include age, veteran status, accommodations information, gender identity and expression,
							sexual orientation, and religion.</p>
					</td>
					<td>
						<p>We do not Sell or Share this category of Personal Information.</p>
					</td>
				</tr>
				<tr>
					<td>
						<p>Commercial information, including records of personal property, products or services
							purchased, obtained, or considered, or other purchasing or consuming histories or
							tendencies.</p>
					</td>
					<td>
						<p>Third-party marketers, analytics providers and other third parties that set cookies and other
							online tracking technologies on our Site.</p>
					</td>
				</tr>
				<tr>
					<td>
						<p>Internet and other electronic network activity information, including, but not limited to,
							browsing history, search history, and information about individual interactions with an
							Internet website, application, or advertisement.</p>
					</td>
					<td>
						<p>Third-party marketers, analytics providers and other third parties that set cookies and other
							online tracking technologies on our Site.</p>
					</td>
				</tr>
				<tr>
					<td>
						<p>Geolocation data, such as your IP address.</p>
					</td>
					<td>
						<p>Third-party marketers, analytics providers and other third parties that set cookies and other
							online tracking technologies on our Site.</p>
					</td>
				</tr>
				<tr>
					<td>
						<p>Professional or employment-related information.</p>
					</td>
					<td>
						<p>Third-party marketers, analytics providers and other third parties that set cookies and other
							online tracking technologies on our Site.</p>
					</td>
				</tr>
				<tr>
					<td>
						<p>Non-public education information as defined in the Family Educational Rights and Privacy Act
							(20 U.S.C. § 1232g; 34 C.F.R. Part 99)</p>
					</td>
					<td>
						<p>We do not Sell or Share this category of Personal Information.</p>
					</td>
				</tr>
				<tr>
					<td>
						<p>Inferences drawn from any of the information categories to create a profile about a consumer
							reflecting the consumer’s preferences, characteristics, psychological trends,
							predispositions, behavior, attitudes, intelligence, abilities, and aptitudes.</p>
					</td>
					<td>
						<p>We do not Sell or Share this category of Personal Information.</p>
					</td>
				</tr>
				<tr>
					<td>
						<p>Sensitive Personal Information as set forth in Cal. Civ. Code § 1798.140, including social
							security number, driver’s license number, state identification card, or passport number;
							citizenship or immigration Information; racial or ethnic origin; or account access
							credentials (like usernames or account numbers combined with password or other
							security/access code to access an account).</p>
					</td>
					<td>
						<p>We do not Sell or Share this category of Personal Information.</p>
					</td>
				</tr>
				</tbody>
			</table>

			<br/>

			<p>California Shine the Light - Under California Civil Code Section 1798.83, individual customers who
				reside in California and who have an existing business relationship with us may request information
				about our disclosure of certain categories of Personal Information to third parties for the third
				parties' direct marketing purposes, if any. To make such a request, please contact us at
				<a href="mailto:customerservice@checkworks.com\">&#32;customerservice@checkworks.com</a>. Please be aware
				that not all information sharing is covered by these California privacy rights requirements and only
				information on covered sharing will be included in our response. This request may be made no more than
				once per calendar year.</p>

			<p><strong>Changes to this Notice</strong></p>

			<p>Please note that we may modify or update this Notice from time to time, so please review it periodically.
				If we make material changes to how we treat Personal Information, we will notify you according to
				applicable law. Unless otherwise indicated, any changes to this Notice will apply immediately upon
				posting to our Site. You are responsible for periodically visiting our Site and this Notice to check for
				any changes.</p>

			<p><strong>Contact Us</strong></p>

			<p>If you have any questions about our practices or this Notice, please contact us at:</p>
			<p>CheckWorks, Inc.</p>
			<p>PO Box 12245</p>
			<p>Birmingham, AL 35202</p>
			<p><a href="mailto:customerservice@checkworks.com\">customerservice@checkworks.com</a></p>
			<p>(866) 870-5918</p>
		</span>
	)
}